import dayjs from "dayjs";
import { Line, type LineConfig } from "@ant-design/plots";
import { AppCard } from "@lib/components/AppComponents";
import { Empty, Skeleton } from "antd";
import { useIntl } from "react-intl";
import { UseQueryProps } from "@lib/types";
import { XAILeadsDashboardResponse } from "@lib/types/xAI";
import { useTheme } from "styled-components";
import { useMemo } from "react";

type Props = {
  query?: UseQueryProps<XAILeadsDashboardResponse>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const TftSalesLeadsDailyChart = (props: Props) => {
  const { messages } = useIntl();
  const { mode } = useTheme();

  const { query } = props;

  const { data, isLoading, isFetching } = query || {};
  const dailyLeadCount = data?.data?.dailyLeadCount || [];
  const chartData = useMemo(() => {
    if (!dailyLeadCount || dailyLeadCount.length === 0) return [];

    return dailyLeadCount
      .map((item) => {
        const date = dayjs(item.lead_date);
        return {
          date: date.format("DD/MM/YYYY"),
          value: item.count,
          dateValue: date.valueOf(),
        };
      })
      .sort((a, b) => a.dateValue - b.dateValue);
  }, [dailyLeadCount]);

  const config: LineConfig = {
    theme: {
      type: mode,
    },
    data: chartData,

    yField: "value",
    xField: "date",
    axis: {
      x: {
        labelAutoRotate: false,
        title: messages["common.date"] as string,
        labelFontSize: 12,
        labelFormatter: (d) => `${d}`,
      },
      y: {
        title: messages["common.count"] as string,
        labelFontSize: 12,
        labelFormatter: (d) => `${d}`,
      },
    },

    tooltip: {
      items: [
        (datum, index, data, column) => ({
          value: `${column.y.value[index]}`,
        }),
      ],
    },

    autoFit: true,
    slider: {
      x: {
        labelFormatter: (d) => `${dayjs(d).format("DD/MM/YYYY")}`,
      },
      y: {
        labelFormatter: (d) => `${d}`,
      },
    },
  };

  return (
    <AppCard title={messages["common.dailyLeadsChart"] as string}>
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {chartData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Line {...config} />
          )}
        </>
      )}
    </AppCard>
  );
};

export default TftSalesLeadsDailyChart;
